import { GameEngine, GameEvent, clone, err } from "../engine.js";
import { RngState } from "../rng.js";
import { Body, Table, clampNum, simulate } from "../physics.js";

/**
 * Carrom — Samra rules v1 (docs/games/carrom.md). 2 players:
 * seat 0 = white coins, shoots from the bottom baseline; seat 1 = black, top.
 * Pocket your own coin to shoot again. Pocketing the striker is a foul: the
 * coins you pocketed on that shot plus one penalty coin go back to the centre.
 * The queen must be covered by pocketing one of your coins on the same or
 * the next shot, and must be covered before your last coin.
 */

export const CARROM_TABLE: Table = {
  w: 740,
  h: 740,
  pockets: [
    { x: 22, y: 22, r: 30 }, { x: 718, y: 22, r: 30 },
    { x: 22, y: 718, r: 30 }, { x: 718, y: 718, r: 30 },
  ],
  friction: 320,
  cushion: 0.7,
  restitution: 0.9,
};
export const COIN_R = 16;
export const STRIKER_R = 21;
const MAX_SPEED = 2200;
const BASE_Y = [640, 100];
const BASE_X: [number, number] = [160, 580];
const C = 370;

export interface CarromCoin { id: number; x: number; y: number; pocketed: boolean }
export interface CarromState {
  n: 2;
  coins: CarromCoin[]; // 1 queen, 2..10 white, 11..19 black
  turn: number;
  queen: { coveredBy: number | null; pendingCover: number | null };
  over: boolean;
  winner: number | null;
  points: number[];
  rng: RngState;
  shots: number;
}
export type CarromMove = { type: "shoot"; x: number; angle: number; power: number };
export type CarromView = Omit<CarromState, "rng"> & { baseline: { y: number; x: [number, number] } };

export const coinOwner = (id: number) => (id === 1 ? -1 : id <= 10 ? 0 : 1);

function setup(): CarromCoin[] {
  const coins: CarromCoin[] = [{ id: 1, x: C, y: C, pocketed: false }];
  let w = 2;
  let b = 11;
  const ring = (count: number, rad: number, off: number) => {
    for (let k = 0; k < count; k++) {
      const a = off + (k * 2 * Math.PI) / count;
      const id = k % 2 === 0 ? w++ : b++;
      coins.push({ id, x: C + rad * Math.cos(a), y: C + rad * Math.sin(a), pocketed: false });
    }
  };
  ring(6, 33, 0);
  ring(12, 66, Math.PI / 12);
  return coins;
}

function returnToCentre(s: CarromState, id: number) {
  const coin = s.coins.find((c) => c.id === id)!;
  for (let r = 0; r < 200; r += 6) {
    for (let a = 0; a < Math.PI * 2; a += 0.4) {
      const x = C + r * Math.cos(a);
      const y = C + r * Math.sin(a);
      if (s.coins.every((c) => c.pocketed || c.id === id || Math.hypot(c.x - x, c.y - y) > COIN_R * 2 + 1)) {
        Object.assign(coin, { x, y, pocketed: false });
        return;
      }
    }
  }
  Object.assign(coin, { x: C, y: C, pocketed: false });
}

const left = (s: CarromState, seat: number) => s.coins.filter((c) => !c.pocketed && coinOwner(c.id) === seat);

export const carrom: GameEngine<CarromState, CarromMove, CarromView, {}> = {
  id: "carrom",
  minPlayers: 2,
  maxPlayers: 2,
  playerCounts: [2],
  defaultOptions: {},

  init(_n, seed) {
    return {
      n: 2, coins: setup(), turn: 0, queen: { coveredBy: null, pendingCover: null },
      over: false, winner: null, points: [0, 0], rng: { s: seed | 0 }, shots: 0,
    };
  },

  currentSeat: (s) => (s.over ? null : s.turn),

  applyMove(state, seat, move) {
    if (state.over) return err("game_over");
    if (seat !== state.turn) return err("not_your_turn");
    if (move?.type !== "shoot") return err("unknown_move");
    const x = clampNum(move.x, BASE_X[0], BASE_X[1]);
    const angle = clampNum(move.angle, -Math.PI * 4, Math.PI * 4);
    const power = clampNum(move.power, 0.05, 1);
    if (x == null || angle == null || power == null) return err("bad_shot");
    const s = clone(state);
    const ev: GameEvent[] = [];
    const sy = BASE_Y[seat];
    if (s.coins.some((c) => !c.pocketed && Math.hypot(c.x - x, c.y - sy) < COIN_R + STRIKER_R))
      return err("striker_overlaps_coin");

    const bodies: Body[] = s.coins.filter((c) => !c.pocketed).map((c) => ({
      id: c.id, x: c.x, y: c.y, vx: 0, vy: 0, r: COIN_R, m: 1, pocketed: false,
    }));
    bodies.push({ id: 0, x, y: sy, vx: Math.cos(angle) * power * MAX_SPEED, vy: Math.sin(angle) * power * MAX_SPEED, r: STRIKER_R, m: 1.6, pocketed: false });
    const sim = simulate(bodies, CARROM_TABLE, 0);
    for (const b of bodies) {
      if (b.id === 0) continue;
      const c = s.coins.find((k) => k.id === b.id)!;
      c.x = b.x; c.y = b.y; c.pocketed = b.pocketed;
    }
    s.shots++;
    ev.push({ type: "shot", seat, x, angle, power, frames: sim.frames, pocketed: sim.pocketed });

    const foul = sim.pocketed.includes(0);
    const ownPotted = sim.pocketed.filter((id) => id !== 0 && coinOwner(id) === seat);
    const queenPotted = sim.pocketed.includes(1);
    let keep = false;

    if (foul) {
      ev.push({ type: "foul", seat, foul: "striker_pocketed" });
      for (const id of ownPotted) returnToCentre(s, id);
      const penalty = s.coins.find((c) => c.pocketed && coinOwner(c.id) === seat);
      if (penalty) returnToCentre(s, penalty.id);
      if (queenPotted || s.queen.pendingCover === seat) {
        returnToCentre(s, 1);
        s.queen.pendingCover = null;
      }
    } else {
      if (queenPotted) {
        if (ownPotted.length) {
          s.queen.coveredBy = seat;
          ev.push({ type: "queen_covered", seat });
        } else {
          s.queen.pendingCover = seat;
          ev.push({ type: "queen_pocketed", seat });
        }
        keep = true;
      } else if (s.queen.pendingCover === seat) {
        if (ownPotted.length) {
          s.queen.coveredBy = seat;
          ev.push({ type: "queen_covered", seat });
        } else {
          returnToCentre(s, 1);
          ev.push({ type: "queen_returned", seat });
        }
        s.queen.pendingCover = null;
      }
      if (ownPotted.length) keep = true;
      // last coin before the queen is covered → return it
      for (const p of [0, 1]) {
        if (left(s, p).length === 0 && s.queen.coveredBy === null) {
          const last = sim.pocketed.filter((id) => coinOwner(id) === p).pop();
          if (last !== undefined) returnToCentre(s, last);
          if (p === seat) keep = false;
          ev.push({ type: "queen_first", seat: p });
        }
      }
    }

    for (const p of [0, 1]) {
      if (left(s, p).length === 0) {
        s.over = true;
        s.winner = p;
        s.points[p] = left(s, 1 - p).length + (s.queen.coveredBy === p ? 3 : 0);
        ev.push({ type: "board_won", winner: p, points: s.points[p] });
        return { ok: true, state: s, events: ev };
      }
    }
    if (!keep) s.turn = 1 - seat;
    return { ok: true, state: s, events: ev };
  },

  view(s, seat) {
    const { rng: _r, ...rest } = s;
    const t = seat === 0 || seat === 1 ? seat : s.turn;
    return { ...clone(rest), baseline: { y: BASE_Y[t], x: BASE_X } };
  },

  result(s) {
    if (!s.over || s.winner == null) return null;
    return { ranking: [[s.winner], [1 - s.winner]], scores: s.points.slice(), reason: "all_coins_pocketed" };
  },

  autoMove(s, seat) {
    if (s.over || s.turn !== seat) return null;
    const sy = BASE_Y[seat];
    const target = left(s, seat)[0] ?? s.coins.find((c) => !c.pocketed);
    for (let x = 370; x <= BASE_X[1]; x += 20) {
      if (s.coins.every((c) => c.pocketed || Math.hypot(c.x - x, c.y - sy) >= COIN_R + STRIKER_R)) {
        const a = target ? Math.atan2(target.y - sy, target.x - x) : seat === 0 ? -Math.PI / 2 : Math.PI / 2;
        return { type: "shoot", x, angle: a, power: 0.5 };
      }
    }
    return { type: "shoot", x: BASE_X[0], angle: seat === 0 ? -Math.PI / 2 : Math.PI / 2, power: 0.5 };
  },
};
